import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import client, { formatErr } from "../lib/api";
import { ArrowLeft, Coins, Clock, Users, Check, X, Share2, Trophy, AlertTriangle } from "lucide-react";

function timeLeft(deadline) {
    const ms = new Date(deadline).getTime() - Date.now();
    if (ms <= 0) return "Ended";
    const h = Math.floor(ms / 3600000);
    const m = Math.floor((ms % 3600000) / 60000);
    const s = Math.floor((ms % 60000) / 1000);
    if (h >= 24) return `${Math.floor(h / 24)}d ${h % 24}h`;
    if (h > 0) return `${h}h ${m}m`;
    if (m > 0) return `${m}m ${s}s`;
    return `${s}s`;
}

export default function ParleyDetail() {
    const { id } = useParams();
    const nav = useNavigate();
    const [p, setP] = useState(null);
    const [busy, setBusy] = useState(false);
    const [, force] = useState(0);

    const load = async () => {
        try {
            const r = await client.get(`/parleys/${id}`);
            setP(r.data);
        } catch (e) { toast.error(formatErr(e)); nav("/app/parleys"); }
    };
    useEffect(() => { load(); }, [id]);
    useEffect(() => {
        const t = setInterval(() => force((v) => v + 1), 1000);
        return () => clearInterval(t);
    }, []);

    if (!p) return <div className="px-5 pt-10 text-zinc-500 text-sm">Loading...</div>;

    const action = async (path, opts = {}) => {
        setBusy(true);
        try {
            const r = await client.post(`/parleys/${id}${path}`, opts.body || {});
            setP(r.data);
            opts.toast && toast.success(opts.toast);
        } catch (e) { toast.error(formatErr(e)); }
        finally { setBusy(false); }
    };

    const copyShare = () => {
        navigator.clipboard.writeText(p.code).then(
            () => toast.success("Code copied"),
            () => toast.error("Copy failed")
        );
    };

    const pot = p.pot_total ?? (p.stake_amount * (p.member_ids || []).length);
    const deadlinePassed = new Date(p.deadline_at).getTime() <= Date.now();

    return (
        <div className="px-5 pt-6 pb-28" data-testid="parley-detail">
            <button onClick={() => nav("/app/parleys")} className="text-zinc-400 flex items-center gap-1 text-xs mb-3">
                <ArrowLeft size={14} /> Parleys
            </button>

            <div className="surface-2 p-5">
                <div className="flex items-center justify-between">
                    <span className="font-mono text-xs text-zinc-500 tracking-widest" data-testid="parley-code">{p.code}</span>
                    <button onClick={copyShare} className="text-neon-green text-xs flex items-center gap-1" data-testid="parley-share">
                        <Share2 size={12} /> Share
                    </button>
                </div>
                <h1 className="font-display font-black text-2xl uppercase mt-2">{p.title}</h1>
                {p.description && <p className="text-sm text-zinc-400 mt-1">{p.description}</p>}

                <div className="mt-4 grid grid-cols-3 gap-3">
                    <Stat label="Stake" value={p.stake_amount} icon={Coins} color="text-neon-green" />
                    <Stat label="Pot" value={pot} icon={Trophy} color="text-neon-orange" />
                    <Stat label="Time Left" value={timeLeft(p.deadline_at)} icon={Clock} color="text-zinc-300" small />
                </div>

                <div className="mt-4 surface-1 p-3">
                    <div className="label-cap text-[10px] text-zinc-500">Target</div>
                    <div className="font-bold mt-1">
                        {p.target_type === "reps"
                            ? `${p.target_count} reps`
                            : p.target_type === "duration_seconds"
                              ? `${p.target_count} seconds`
                              : `${(p.target_count / 1000).toFixed(2)} km`} of <span className="text-neon-green">{p.target_exercise}</span>
                    </div>
                </div>
            </div>

            <div className="mt-5">
                <div className="flex items-center justify-between mb-3">
                    <div>
                        <span className="label-cap text-[10px] text-zinc-500 flex items-center gap-1">
                            <Users size={11} /> Members {(p.members || []).length}/{p.max_members}
                        </span>
                    </div>
                    <span className="label-cap text-[10px] text-zinc-500">status: <span className="text-neon-green">{p.status}</span></span>
                </div>
                <div className="space-y-2" data-testid="parley-members">
                    {(p.members || []).map((m) => {
                        const completed = (p.completer_ids || []).includes(m.user_id);
                        const payout = p.payouts?.[m.user_id];
                        return (
                            <div key={m.user_id} className="surface-1 p-3 flex items-center gap-3">
                                <div className="w-8 h-8 bg-charcoal-700 flex items-center justify-center text-xs font-bold">
                                    {(m.name || "?")[0]}
                                </div>
                                <div className="flex-1 min-w-0">
                                    <div className="text-sm font-bold truncate">{m.name}</div>
                                    <div className="text-[10px] text-zinc-500 uppercase tracking-wider">
                                        staked {m.stake_paid} $NoEx
                                    </div>
                                </div>
                                {p.status === "resolved" ? (
                                    payout ? (
                                        <span className="label-cap text-[10px] bg-neon-green text-black px-2 py-0.5">+{payout}</span>
                                    ) : (
                                        <span className="label-cap text-[10px] text-neon-orange">Forfeited</span>
                                    )
                                ) : completed ? (
                                    <Check size={16} className="text-neon-green" />
                                ) : (
                                    <span className="text-[10px] text-zinc-600 uppercase">Pending</span>
                                )}
                            </div>
                        );
                    })}
                </div>
            </div>

            <div className="mt-6 space-y-2">
                {p.status === "open" && p.is_creator && (
                    <>
                        <button
                            onClick={() => action("/lock", { toast: "Locked — deadline timer started" })}
                            disabled={busy}
                            data-testid="parley-lock"
                            className="btn-neon w-full py-3 disabled:opacity-50"
                        >
                            Lock Parley & Start Clock
                        </button>
                        <button
                            onClick={() => action("/cancel", { toast: "Cancelled — stakes refunded" })}
                            disabled={busy}
                            data-testid="parley-cancel"
                            className="surface-1 w-full py-2 text-xs label-cap text-neon-orange"
                        >
                            Cancel & Refund Everyone
                        </button>
                    </>
                )}
                {p.status === "open" && !p.is_creator && p.is_member && (
                    <button
                        onClick={() => action("/leave", { toast: "Left parley — stake refunded" })}
                        disabled={busy}
                        data-testid="parley-leave"
                        className="surface-1 w-full py-2 text-xs label-cap text-neon-orange"
                    >
                        Leave (refunded)
                    </button>
                )}
                {p.status === "locked" && p.is_member && !p.is_completer && (
                    <>
                        <button
                            onClick={() => action("/mark-complete", { toast: "Marked complete!" })}
                            disabled={busy}
                            data-testid="parley-mark-complete"
                            className="btn-neon w-full py-3 disabled:opacity-50"
                        >
                            I Completed It {["run","walk","running","walking"].includes(p.target_exercise) ? "(needs logged run)" : "(needs approved submission)"}
                        </button>
                        {["run","walk","running","walking"].includes(p.target_exercise) ? (
                            <Link
                                to={`/app/runs?parley=${p.id}`}
                                data-testid="parley-track-run"
                                className="surface-1 w-full py-2 text-xs label-cap text-zinc-300 flex items-center justify-center"
                            >
                                Track a {p.target_exercise} →
                            </Link>
                        ) : (
                            <Link
                                to="/app/challenges"
                                className="surface-1 w-full py-2 text-xs label-cap text-zinc-300 flex items-center justify-center"
                            >
                                Submit a proof video →
                            </Link>
                        )}
                    </>
                )}
                {p.status === "locked" && p.is_completer && (
                    <div className="surface-1 p-3 flex items-center gap-2">
                        <Check size={14} className="text-neon-green" />
                        <span className="text-xs text-zinc-300">You completed — payout on resolve.</span>
                    </div>
                )}
                {p.status === "locked" && deadlinePassed && (
                    <button
                        onClick={() => action("/resolve", { toast: "Resolved & paid out" })}
                        disabled={busy}
                        data-testid="parley-resolve"
                        className="btn-neon w-full py-3 disabled:opacity-50"
                    >
                        Resolve Now (deadline passed)
                    </button>
                )}
                {p.status === "resolved" && (
                    <div className="surface-1 p-4 text-center">
                        <Trophy size={20} className="text-neon-orange mx-auto" />
                        <div className="text-sm mt-2">
                            {(p.completer_ids || []).length > 0
                                ? `${(p.completer_ids || []).length} completer${(p.completer_ids || []).length > 1 ? "s" : ""} cashed out.`
                                : "House took the pot — nobody completed."}
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}

function Stat({ label, value, icon: Icon, color, small }) {
    return (
        <div className="surface-1 p-3">
            <div className="label-cap text-[9px] text-zinc-500 flex items-center gap-1">
                <Icon size={10} /> {label}
            </div>
            <div className={`font-display font-black ${small ? "text-base" : "text-xl"} ${color} mt-1`}>{value}</div>
        </div>
    );
}
